import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, ButtonBase, CircularProgress, IconButton, TableCell, TableRow, Typography } from "@mui/material";
import { RiArrowDownSLine, RiArrowUpSLine, RiExpandUpDownLine, RiEyeLine } from "@remixicon/react";
import { focusRing, tokens } from "../../utils/figmaTokens";
import InitialsAvatar from "./InitialsAvatar";
import { EmptyState, ErrorState } from "./States";

// Table Cell "User" (10599:316): a 32 px avatar, the name, an optional grey line under it.
export const PersonCell = ({ name, avatarName = name, colorKey, sub }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
    <InitialsAvatar name={avatarName} colorKey={colorKey} size={32} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="bodySmallStrong" noWrap sx={{ color: tokens.text.primary }}>
        {name}
      </Typography>
      {sub && (
        <Typography variant="labelMedium" component="div" noWrap sx={{ color: tokens.text.tertiary }}>
          {sub}
        </Typography>
      )}
    </Box>
  </Box>
);

// Table Cell "Two-line / Date": a value with a grey line under it. `wrap` lets a long value
// (a ticket's reason) break across lines.
export const TwoLineCell = ({ top, bottom, wrap = false }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="bodySmall" sx={{ color: tokens.text.primary, whiteSpace: wrap ? "normal" : "nowrap" }}>
      {top}
    </Typography>
    {bottom && (
      <Typography variant="labelMedium" component="div" sx={{ color: tokens.text.tertiary, whiteSpace: wrap ? "normal" : "nowrap" }}>
        {bottom}
      </Typography>
    )}
  </Box>
);

// A row's icon buttons (Admin · Icon Button 10599:193): 28 px, a 16 px icon. Keyboard focus
// shows D22's ring, not MUI's grey focus ripple (047 D73).
const rowButtonSx = { p: "6px", borderRadius: "7px", color: tokens.icon.default, "&:hover": { bgcolor: tokens.hover }, ...focusRing };

// The row's view action (eye-line): a link.
export const EyeButton = ({ to, label }) => (
  <IconButton component={RouterLink} to={to} aria-label={label} disableFocusRipple sx={rowButtonSx}>
    <RiEyeLine size={16} />
  </IconButton>
);

// Any other row action, with the eye's look (the Students list's pencil; the Actions cell on
// 10641:4377).
export const RowAction = ({ Icon, label, onClick, ...rest }) => (
  <IconButton aria-label={label} onClick={onClick} disableFocusRipple sx={rowButtonSx} {...rest}>
    <Icon size={16} />
  </IconButton>
);

// A column head that sorts (Column Header 10659:68564 with Show sort on): the label, then a
// 14 px glyph at the cell's far edge — both chevrons at 55 % while another column sorts, one
// chevron pointing the current way while this one does. A head that cannot sort is a plain
// TableCell and draws no glyph.
export const SortHead = ({ label, active, direction = "asc", onSort }) => {
  const Glyph = !active ? RiExpandUpDownLine : direction === "desc" ? RiArrowDownSLine : RiArrowUpSLine;
  return (
    <TableCell sortDirection={active ? direction : false}>
      <ButtonBase
        onClick={onSort}
        disableRipple
        sx={{
          width: "100%",
          justifyContent: "space-between",
          gap: "12px",
          font: "inherit",
          letterSpacing: "inherit",
          textTransform: "inherit",
          color: "inherit",
          borderRadius: "4px",
          ...focusRing,
        }}
      >
        {label}
        <Box component="span" aria-hidden sx={{ display: "flex", color: tokens.icon.default, opacity: active ? 1 : 0.55 }}>
          <Glyph size={14} />
        </Box>
      </ButtonBase>
    </TableCell>
  );
};

// Grey bars while a list loads (Users · Loading 10620:3615): `rows` rows, each cell holding one
// 12 px bar as wide as the cell, up to 169 px, so the bars never widen the table.
export const SkeletonRows = ({ columns, rows = 10 }) =>
  Array.from({ length: rows }, (_, row) => (
    <TableRow key={row} aria-hidden>
      {Array.from({ length: columns }, (__, column) => (
        <TableCell key={column}>
          <Box sx={{ width: "100%", maxWidth: 169, height: 12, borderRadius: "999px", bgcolor: tokens.surface.muted }} />
        </TableCell>
      ))}
    </TableRow>
  ));

// The one row a table shows while it loads, after it failed, or when it is empty. A list page
// adds the states' second line and buttons (`errorMessage`, `emptyMessage`, `emptyActions`).
export const TableMessageRow = ({ colSpan, loading, error, onRetry, emptyText, errorMessage, emptyMessage, emptyActions }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ height: "auto", borderBottom: "none" }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={22} />
        </Box>
      ) : error ? (
        <ErrorState title={error} message={errorMessage} onRetry={onRetry} />
      ) : (
        <EmptyState title={emptyText} message={emptyMessage} actions={emptyActions} />
      )}
    </TableCell>
  </TableRow>
);
